/**
 * Which "contact the customer" modes a caller may use on an out-of-stock order,
 * and what the entry button is called.
 *
 * The contact dialog has two modes, and the server gates them differently:
 *
 *   send_email    -> `notifyCX`, on `productionCoordinatorProcedure`: Super
 *                    Admin, Admin, Warehouse Coordinator, AND the caller's org
 *                    must run a floor of its own (that tier's admission rule,
 *                    `requireOwnedLocation`). It emails the customer under the
 *                    caller org's sender, so it is NOT open to CX, and it is
 *                    refused on a row that belongs to ANOTHER org (the
 *                    handler's same-org gate: an owner-branded email to a
 *                    client's customer would be wrong).
 *   mark_manually -> `markCxContactedManually`, on
 *                    `productionCoordinatorOrCxProcedure` with
 *                    `orders.status_override`: the same three roles plus CX.
 *                    Allowed on a client's row at a floor the caller's org owns,
 *                    and open to an org that runs no floor but is a client
 *                    somewhere (that tier's junction-aware admission rule).
 *
 * ⚠ KEEP IN LOCKSTEP with
 * apps/backend/src/modules/production-issues/production-issues.handler.ts and
 * the tiers in apps/backend/src/trpc.ts. The email mode has no permission of
 * its own, so it mirrors ALL THREE of `notifyCX`'s server gates:
 *   1. `canSendEmail`     the tier's role list (admin or Warehouse Coordinator,
 *                         passed from the role helpers);
 *   2. `orgOperatesFloor` the tier's admission rule (`requireOwnedLocation`:
 *                         the org owns a location that is not delegated). The
 *                         frontend twin is `useOrgRoleSurfaces().hidesProduction`,
 *                         inverted;
 *   3. `isOwnOrgRow`      the handler's same-org gate.
 * `canMarkContacted` is `can('orders.status_override')`.
 *
 * Offering a mode the server refuses is a button that returns an error; hiding
 * one the server allows is a job the user has to hand to someone else. The
 * entry is absent altogether when the caller can use neither.
 */

export type ContactMode = 'send_email' | 'mark_manually';

export interface ContactAccess {
  /** False = render no entry at all. */
  showEntry: boolean;
  /** The modes to offer, in display order. The dialog opens on the first;
   *  one mode = no picker. */
  modes: ContactMode[];
  /** i18n key (production namespace) for the entry button. */
  entryLabelKey: 'issues.notifyCx' | 'issues.notifyCx.modeMarkManually';
}

/**
 * Does this row belong to the caller's own organisation?
 *
 * The same comparison the client-org chip uses on the issue lists
 * (`useClientOrgLabels().isCrossOrg`), inverted: a row is another org's only
 * when BOTH ids are known and they differ. An unknown id on either side counts
 * as the caller's own, so nothing is hidden while data is still loading; the
 * server's same-org gate is the authority either way.
 */
export function rowBelongsToCallerOrg(
  callerOrgId: string | null | undefined,
  rowOrgId: string | null | undefined,
): boolean {
  return !(callerOrgId && rowOrgId && rowOrgId !== callerOrgId);
}

export function resolveContactAccess({
  canMarkContacted,
  canSendEmail,
  isOwnOrgRow,
  orgOperatesFloor,
}: {
  canMarkContacted: boolean;
  canSendEmail: boolean;
  /** See `rowBelongsToCallerOrg`. False = the row is another org's. */
  isOwnOrgRow: boolean;
  /**
   * Does the caller's org run a floor of its own (own a location that is not
   * delegated)? False = a pure client, or an org with no location: the email's
   * tier refuses it whatever the role. Pass `!hidesProduction` from
   * `useOrgRoleSurfaces`, which is true (operates) until the answer is known,
   * so nothing is hidden on a loading or failed lookup.
   */
  orgOperatesFloor: boolean;
}): ContactAccess {
  // The email goes out under the caller org's sender, so it is offered only on
  // the caller org's own rows, and only by an org the email's tier admits.
  const offerEmail = canSendEmail && isOwnOrgRow && orgOperatesFloor;

  const modes: ContactMode[] = [];
  if (offerEmail) modes.push('send_email');
  if (canMarkContacted) modes.push('mark_manually');

  return {
    showEntry: modes.length > 0,
    modes,
    // With no email on offer the entry does exactly one thing, so it says so.
    entryLabelKey: offerEmail ? 'issues.notifyCx' : 'issues.notifyCx.modeMarkManually',
  };
}
